import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useAccessControl } from '../hooks/useAccessControl'
import { useDataSync } from '../hooks/useDataSync'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import EnhancedMedicalRecordsManager from '../components/EnhancedMedicalRecordsManager'
import MedicalDocumentUpload from '../components/MedicalDocumentUpload'
import MedicalHistoryTimeline from '../components/MedicalHistoryTimeline'
import PatientAuditViewer from '../components/PatientAuditViewer'
import AccessControlManager from '../components/AccessControlManager'
import PQCSecurityCenter from '../components/PQCSecurityCenter'
import PQCStatusIndicator from '../components/PQCStatusIndicator'
import PasskeyManager from '../components/PasskeyManager'
import RefreshDebugger from '../components/RefreshDebugger'
import { 
  Heart, 
  FileText, 
  Users, 
  Shield, 
  Calendar, 
  Clock, 
  Eye, 
  EyeOff, 
  Download,
  Share2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Plus,
  Activity,
  TrendingUp,
  TrendingDown,
  Thermometer,
  Weight,
  Zap,
  Bell,
  Filter,
  Search,
  BarChart3,
  PieChart,
  LineChart,
  Stethoscope,
  Pill,
  TestTube,
  Brain,
  Bookmark,
  Star,
  Key,
  LogOut,
  ShieldCheck,
  Lock,
  Cpu,
  Database,
  Fingerprint
} from 'lucide-react'
import { toast } from 'sonner'

interface DashboardStats {
  totalRecords: number
  activePermissions: number
  pendingRequests: number
  lastUpdate: string
}

interface HealthMetrics {
  bloodPressure: { systolic: number; diastolic: number; date: string }
  heartRate: { value: number; date: string }
  weight: { value: number; date: string }
  temperature: { value: number; date: string }
}

interface QuickAction {
  id: string
  title: string
  description: string
  icon: React.ReactNode
  color: string
  action: () => void
}

interface MedicalRecord {
  id: string
  title: string
  description?: string
  record_type: string
  diagnosis?: string
  treatment?: string
  created_at: string
  blockchain_hash?: string
  doctor?: {
    full_name: string
  }
}

interface AccessPermission {
  id: string
  status: 'pending' | 'approved' | 'denied' | 'revoked'
  created_at: string
  granted_at?: string
}

export default function PatientDashboard() {
  const { user, userProfile, signOut } = useAuth()
  const { requests, permissions, revalidateData, invalidateAllCache } = useAccessControl()
  const { revalidateAll, forceCompleteRevalidation, hasDetectedRefresh, markDashboardProcessed, isDashboardProcessed } = useDataSync()
  const [stats, setStats] = useState<DashboardStats>({
    totalRecords: 0,
    activePermissions: 0,
    pendingRequests: 0,
    lastUpdate: ''
  })
  const [healthMetrics, setHealthMetrics] = useState<HealthMetrics>({
    bloodPressure: { systolic: 120, diastolic: 80, date: new Date().toISOString() },
    heartRate: { value: 72, date: new Date().toISOString() },
    weight: { value: 70, date: new Date().toISOString() },
    temperature: { value: 36.5, date: new Date().toISOString() }
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all')
  const [showHealthMetrics, setShowHealthMetrics] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')

  // Carregar dados iniciais e após refresh
  useEffect(() => {
    if (user && userProfile?.role === 'patient') {
      console.log('📊 [PatientDashboard] Carregando estatísticas do paciente...');
      
      const pendingRequests = requests?.filter(r => r.status === 'pending').length || 0
      const activePermissions = permissions?.filter(p => 
        p.status === 'approved' && 
        (!p.expires_at || new Date(p.expires_at) > new Date())
      ).length || 0
      
      setStats({
        totalRecords: 12,
        activePermissions,
        pendingRequests,
        lastUpdate: new Date().toISOString()
      })
      
      console.log('📊 [PatientDashboard] Estatísticas atualizadas:', {
        pendingRequests,
        activePermissions,
        totalRecords: 12
      });
    }
  }, [user, userProfile, requests, permissions])

  // Detectar refresh da página e recarregar dados (com controle robusto)
  useEffect(() => {
    if (user && userProfile?.role === 'patient' && hasDetectedRefresh) {
      // Verificar se este dashboard já foi processado para este refresh
      if (isDashboardProcessed('patient')) {
        console.log('⏭️ [PatientDashboard] Refresh já processado para este dashboard, ignorando...');
        return;
      }
      
      console.log('🔄 [PatientDashboard] Refresh detectado - recarregando dados...');
      
      // Marcar como processado imediatamente
      markDashboardProcessed('patient');
      
      // Flag para evitar múltiplas execuções
      const lastRefresh = sessionStorage.getItem('patient_last_refresh_toast');
      const now = Date.now();
      
      // Throttling: só mostrar toast se passou mais de 10 segundos desde o último
      const REFRESH_TOAST_THROTTLE = 10000; // 10 segundos
      const shouldShowToast = !lastRefresh || (now - parseInt(lastRefresh)) > REFRESH_TOAST_THROTTLE;
      
      // Forçar recarregamento de todos os dados do banco
      const reloadAllData = async () => {
        try {
           // Invalidar cache do controle de acesso
           if (invalidateAllCache) {
             invalidateAllCache();
           }
           
           // Revalidar dados do controle de acesso com cache limpo
           if (revalidateData) {
             await revalidateData(true);
           }
           
           // Forçar revalidação completa de todos os sistemas
           await forceCompleteRevalidation();
          
          console.log('✅ [PatientDashboard] Recarregamento completo após refresh finalizado');
          
          // Só mostrar toast se passou tempo suficiente desde o último
          if (shouldShowToast) {
            sessionStorage.setItem('patient_last_refresh_toast', now.toString());
            toast.success('Dados atualizados após recarregamento da página');
            console.log('📢 [PatientDashboard] Toast de refresh exibido (throttled)');
          } else {
            console.log('⏭️ [PatientDashboard] Toast de refresh ignorado (throttling ativo)');
          }
        } catch (error) {
          console.error('❌ [PatientDashboard] Erro ao recarregar dados após refresh:', error);
          toast.error('Erro ao atualizar dados. Tente recarregar a página.');
        }
      };
      
      reloadAllData();
    }
  }, [user, userProfile, hasDetectedRefresh, revalidateData, forceCompleteRevalidation, isDashboardProcessed, markDashboardProcessed])

  // Revalidar dados ao navegar entre abas
  useEffect(() => {
    if (user && userProfile?.role === 'patient') {
      console.log('🔄 [PatientDashboard] Revalidando dados ao mudar aba:', activeTab);
      // Usar sistema centralizado de revalidação
      revalidateAll(true);
    }
  }, [activeTab, user, userProfile, revalidateAll])

  const quickActions: QuickAction[] = [
    {
      id: 'schedule-appointment',
      title: 'Agendar Consulta',
      description: 'Marque uma consulta com seu médico',
      icon: <Calendar className="h-5 w-5" />,
      color: 'bg-blue-500',
      action: () => toast.info('Funcionalidade em desenvolvimento')
    },
    {
      id: 'request-prescription',
      title: 'Solicitar Receita',
      description: 'Peça renovação de receitas médicas',
      icon: <Pill className="h-5 w-5" />,
      color: 'bg-green-500',
      action: () => toast.info('Funcionalidade em desenvolvimento')
    },
    {
      id: 'view-lab-results',
      title: 'Resultados de Exames',
      description: 'Visualize seus exames laboratoriais',
      icon: <TestTube className="h-5 w-5" />,
      color: 'bg-purple-500',
      action: () => toast.info('Funcionalidade em desenvolvimento')
    },
    {
      id: 'emergency-contact',
      title: 'Contato de Emergência',
      description: 'Acesso rápido para emergências',
      icon: <Zap className="h-5 w-5" />,
      color: 'bg-red-500',
      action: () => toast.info('Funcionalidade em desenvolvimento')
    }
  ]

  const getHealthStatus = (metric: string, value: number) => {
    switch (metric) {
      case 'bloodPressure':
        if (value > 140) return { status: 'high', color: 'text-red-600', icon: <TrendingUp className="h-4 w-4" /> }
        if (value < 90) return { status: 'low', color: 'text-blue-600', icon: <TrendingDown className="h-4 w-4" /> }
        return { status: 'normal', color: 'text-green-600', icon: <Activity className="h-4 w-4" /> }
      case 'heartRate':
        if (value > 100) return { status: 'high', color: 'text-red-600', icon: <TrendingUp className="h-4 w-4" /> }
        if (value < 60) return { status: 'low', color: 'text-blue-600', icon: <TrendingDown className="h-4 w-4" /> }
        return { status: 'normal', color: 'text-green-600', icon: <Activity className="h-4 w-4" /> }
      default:
        return { status: 'normal', color: 'text-green-600', icon: <Activity className="h-4 w-4" /> }
    }
  }

  // Dados filtrados simulados
  const filteredRecords: any[] = []

  // Função simplificada para demonstração
  const handlePermissionAction = (permissionId: string, action: 'approve' | 'deny' | 'revoke') => {
    toast.success(`Ação ${action} executada com sucesso!`)
  }

  const getRecordTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      consultation: 'Consulta',
      diagnosis: 'Diagnóstico',
      prescription: 'Prescrição',
      lab_result: 'Exame Laboratorial',
      imaging: 'Exame de Imagem',
      surgery: 'Cirurgia',
      vaccination: 'Vacinação'
    }
    return labels[type] || type
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'denied':
        return <XCircle className="h-5 w-5 text-red-500" />
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />
      case 'revoked':
        return <XCircle className="h-5 w-5 text-gray-500" />
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Não renderizar loading aqui - deixar o AuthContext gerenciar

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Dashboard do Paciente</h1>
                <p className="text-gray-600">Bem-vindo, {userProfile?.full_name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <PQCStatusIndicator variant="compact" />
              <button
                onClick={signOut}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Fazer logout"
              >
                <LogOut className="h-4 w-4" />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'overview', name: 'Visão Geral', icon: <BarChart3 className="h-5 w-5" /> },
                { id: 'records', name: 'Registros Médicos', icon: <FileText className="h-5 w-5" /> },
                { id: 'health', name: 'Métricas de Saúde', icon: <Activity className="h-5 w-5" /> },
                { id: 'access', name: 'Controle de Acesso', icon: <Lock className="h-5 w-5" /> },
                { id: 'audit', name: 'Meus Acessos', icon: <ShieldCheck className="h-5 w-5" /> },
                { id: 'passkeys', name: 'Passkeys', icon: <Fingerprint className="h-5 w-5" /> },
                { id: 'security', name: 'Segurança PQC', icon: <Cpu className="h-5 w-5" /> }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                >
                  {tab.icon}
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <FileText className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total de Registros</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.totalRecords}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <Users className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Acessos Ativos</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.activePermissions}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="bg-yellow-100 p-3 rounded-lg">
                    <Clock className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Solicitações Pendentes</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.pendingRequests}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <Calendar className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Última Atualização</p>
                    <p className="text-sm font-bold text-gray-900">
                      {stats.lastUpdate ? formatDate(stats.lastUpdate) : 'Nenhuma'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {quickActions.map((action) => (
                  <button
                    key={action.id}
                    onClick={action.action}
                    className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow text-left"
                  >
                    <div className="flex items-center mb-3">
                      <div className={`${action.color} p-2 rounded-lg text-white`}>
                        {action.icon}
                      </div>
                      <h4 className="ml-3 font-medium text-gray-900">{action.title}</h4>
                    </div>
                    <p className="text-sm text-gray-600">{action.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Health Metrics Overview */}
            <div className="bg-white rounded-lg shadow mb-8">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">Métricas de Saúde</h3>
                  <button
                    onClick={() => setActiveTab('health')}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Ver detalhes
                  </button>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Heart className="h-8 w-8 text-red-500" />
                    </div>
                    <p className="text-sm text-gray-600">Pressão Arterial</p>
                    <p className="text-lg font-bold text-gray-900">
                      {healthMetrics.bloodPressure.systolic}/{healthMetrics.bloodPressure.diastolic}
                    </p>
                    <div className="flex items-center justify-center mt-1">
                      {getHealthStatus('bloodPressure', healthMetrics.bloodPressure.systolic).icon}
                      <span className={`ml-1 text-xs ${getHealthStatus('bloodPressure', healthMetrics.bloodPressure.systolic).color}`}>
                        Normal
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Activity className="h-8 w-8 text-blue-500" />
                    </div>
                    <p className="text-sm text-gray-600">Frequência Cardíaca</p>
                    <p className="text-lg font-bold text-gray-900">{healthMetrics.heartRate.value} bpm</p>
                    <div className="flex items-center justify-center mt-1">
                      {getHealthStatus('heartRate', healthMetrics.heartRate.value).icon}
                      <span className={`ml-1 text-xs ${getHealthStatus('heartRate', healthMetrics.heartRate.value).color}`}>
                        Normal
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Weight className="h-8 w-8 text-green-500" />
                    </div>
                    <p className="text-sm text-gray-600">Peso</p>
                    <p className="text-lg font-bold text-gray-900">{healthMetrics.weight.value} kg</p>
                    <div className="flex items-center justify-center mt-1">
                      <Activity className="h-4 w-4 text-green-600" />
                      <span className="ml-1 text-xs text-green-600">Estável</span>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Thermometer className="h-8 w-8 text-orange-500" />
                    </div>
                    <p className="text-sm text-gray-600">Temperatura</p>
                    <p className="text-lg font-bold text-gray-900">{healthMetrics.temperature.value}°C</p>
                    <div className="flex items-center justify-center mt-1">
                      <Activity className="h-4 w-4 text-green-600" />
                      <span className="ml-1 text-xs text-green-600">Normal</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Records Tab */}
        {activeTab === 'records' && (
          <EnhancedMedicalRecordsManager
                  patientId={user?.id}
                  mode="patient"
                  showCreateButton={false}
                  defaultView="history"
                />
        )}

        {/* Health Metrics Tab */}
        {activeTab === 'health' && (
          <>
            {/* Detailed Health Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Vital Signs */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Sinais Vitais</h3>
                </div>
                <div className="p-6 space-y-6">
                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                    <div className="flex items-center">
                      <Heart className="h-8 w-8 text-red-500 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">Pressão Arterial</p>
                        <p className="text-sm text-gray-600">Última medição: hoje</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">
                        {healthMetrics.bloodPressure.systolic}/{healthMetrics.bloodPressure.diastolic}
                      </p>
                      <p className="text-sm text-green-600">Normal</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center">
                      <Activity className="h-8 w-8 text-blue-500 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">Frequência Cardíaca</p>
                        <p className="text-sm text-gray-600">Última medição: hoje</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">{healthMetrics.heartRate.value}</p>
                      <p className="text-sm text-green-600">bpm - Normal</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center">
                      <Weight className="h-8 w-8 text-green-500 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">Peso</p>
                        <p className="text-sm text-gray-600">Última medição: hoje</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">{healthMetrics.weight.value}</p>
                      <p className="text-sm text-green-600">kg - Estável</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                    <div className="flex items-center">
                      <Thermometer className="h-8 w-8 text-orange-500 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">Temperatura</p>
                        <p className="text-sm text-gray-600">Última medição: hoje</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">{healthMetrics.temperature.value}</p>
                      <p className="text-sm text-green-600">°C - Normal</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Health Trends */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Tendências de Saúde</h3>
                </div>
                <div className="p-6">
                  <div className="text-center py-8">
                    <LineChart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">Gráficos de tendências</p>
                    <p className="text-sm text-gray-400">Visualização de dados históricos em desenvolvimento</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Health Recommendations */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Recomendações de Saúde</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center mb-2">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                      <h4 className="font-medium text-green-900">Pressão Arterial</h4>
                    </div>
                    <p className="text-sm text-green-700">Seus níveis estão dentro do normal. Continue mantendo uma dieta equilibrada.</p>
                  </div>
                  
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center mb-2">
                      <CheckCircle className="h-5 w-5 text-blue-600 mr-2" />
                      <h4 className="font-medium text-blue-900">Frequência Cardíaca</h4>
                    </div>
                    <p className="text-sm text-blue-700">Excelente! Sua frequência cardíaca indica boa condição cardiovascular.</p>
                  </div>
                  
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <div className="flex items-center mb-2">
                      <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
                      <h4 className="font-medium text-yellow-900">Hidratação</h4>
                    </div>
                    <p className="text-sm text-yellow-700">Lembre-se de beber pelo menos 2 litros de água por dia.</p>
                  </div>
                  
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center mb-2">
                      <Brain className="h-5 w-5 text-purple-600 mr-2" />
                      <h4 className="font-medium text-purple-900">Bem-estar Mental</h4>
                    </div>
                    <p className="text-sm text-purple-700">Pratique exercícios de relaxamento e mantenha uma rotina de sono regular.</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Access Control Tab */}
        {activeTab === 'access' && (
          <AccessControlManager />
        )}

        {/* Patient Audit Tab */}
        {activeTab === 'audit' && (
          <PatientAuditViewer />
        )}

        {/* Passkeys Tab */}
        {activeTab === 'passkeys' && (
          <PasskeyManager userId={user?.id} />
        )}

        {/* Security PQC Tab */}
        {activeTab === 'security' && (
          <PQCSecurityCenter />
        )}
      </div>
    </div>
  )
}